import React from 'react';
import type { StepConfig } from '../../types';

type UnknownRecord = Record<string, unknown>;

type FormInstance = {
  instanceId?: string;
  data?: UnknownRecord;
  lastStepIndex?: number;
};

type CompletedForms = Record<string, FormInstance[]>;

type Props = {
  values?: UnknownRecord;
  completedForms?: CompletedForms;
  allSteps: StepConfig[];
};
type SectionRendererProps = {
  formInstances: FormInstance[];
  groupKey: string;
  allSteps: StepConfig[];
  showSectionHeader?: boolean;
};
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function isPlainObject(v: unknown): v is UnknownRecord {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string') return v.trim() === '' ? '—' : v;
  if (typeof v === 'number') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function capitalizeWords(value: string): string {
  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getSectionTitleByGroupKey(
  allSteps: StepConfig[],
  groupKey: string
): string | undefined {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          for (const card of section.cards) {
            if (card.groupKey === groupKey) {
              return section.title;
            }
          }
        }
      }
    }
  }
  return undefined;
}

export function getCardTitleByGroupKey(
  allSteps: StepConfig[],
  groupKey: string
): string | undefined {
  for (const step of allSteps) {
    if (step.sections) {
      for (const section of step.sections) {
        if (section.cards) {
          for (const card of section.cards) {
            if (card.groupKey === groupKey) {
              return card.title;
            }
          }
        }
        // Legacy: check if section has groupKey matching (for backward compatibility)
        const sectionGroupKey = section.groupKey || section.id;
        if (sectionGroupKey === groupKey) {
          return section.title;
        }
      }
    }
  }
  return undefined;
}
export function getFieldLabelByFieldId(
  allSteps: StepConfig[],
  fieldId: string
): string | undefined {
  for (const step of allSteps) {
    if (step.fieldGroups) {
      for (const fieldGroup of step.fieldGroups) {
        if (fieldGroup.fields) {
          for (const field of fieldGroup.fields) {
            if (field.id === fieldId) {
              return field.label;
            }
          }
        }
      }
    }
  }
  return undefined;
}

type EmptyGroupPlaceholderProps = {
  allSteps: StepConfig[];
  groupKey: string;
  showSectionHeader?: boolean;
};

/** Shown when a repeatable estate group has no instances (e.g. user chose not to add charities/beneficiaries). */
const EmptyGroupPlaceholder = ({
  allSteps,
  groupKey,
  showSectionHeader = false,
}: EmptyGroupPlaceholderProps) => {
  const sectionTitle = getSectionTitleByGroupKey(allSteps, groupKey);
  const cardTitle = getCardTitleByGroupKey(allSteps, groupKey) ?? groupKey;
  return (
    <table width="100%" border={0} cellPadding={0} cellSpacing={0} align="left">
      <tbody>
        {showSectionHeader && sectionTitle && (
          <tr>
            <td
              colSpan={2}
              align="left"
              style={{ backgroundColor: '#0c1b39', color: '#ffffff', padding: '10px' }}
            >
              {escapeHtml(fmt(sectionTitle))}
            </td>
          </tr>
        )}
        <tr>
          <td
            style={{
              backgroundColor: '#f8f6f1',
              color: '#0c1b39',
              padding: '10px',
              borderTop: '1px solid #666666',
            }}
            width="30%"
            align="left"
            colSpan={2}
          >
            <strong>{escapeHtml(fmt(cardTitle))}</strong>
          </td>
        </tr>
        <tr>
          <td
            style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
            colSpan={2}
            align="left"
          >
            None added
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export const SectionRenderer = ({
  formInstances,
  groupKey,
  allSteps,
  showSectionHeader = false,
}: SectionRendererProps) => {
  if (!formInstances?.length) return null;

  return (
    <table width="100%" border={0} cellPadding={0} cellSpacing={0} align="left">
      <tbody>
        {showSectionHeader && (
          <tr>
            <td
              colSpan={2}
              align="left"
              style={{ backgroundColor: '#0c1b39', color: '#ffffff', padding: '10px' }}
            >
              {escapeHtml(fmt(getSectionTitleByGroupKey(allSteps, groupKey)))}
            </td>
          </tr>
        )}
        {formInstances.map((inst, idx) => {
          const dataObject: Record<string, unknown> = isPlainObject(inst.data) ? inst.data : {};
          const isExecutorChoiceGroup = groupKey === 'executor-group-choice';
          const isBeneficiaryGroup =
            groupKey === 'children-group' ||
            groupKey === 'spousePartnerChildrenFromPreviousRelationship-group';

          const name = isBeneficiaryGroup
            ? groupKey === 'children-group'
              ? [dataObject.childFirstName, dataObject.childMiddleName, dataObject.childLastName]
                  .filter(Boolean)
                  .join(' ')
              : [
                  dataObject.spousePartnerChildFirstName,
                  dataObject.spousePartnerChildMiddleName,
                  dataObject.spousePartnerChildLastName,
                ]
                  .filter(Boolean)
                  .join(' ')
            : '';
          const relationship = isBeneficiaryGroup
            ? groupKey === 'children-group'
              ? dataObject.childRelationship
              : dataObject.spousePartnerChildRelationship
            : '';
          const dob = isBeneficiaryGroup
            ? groupKey === 'children-group'
              ? dataObject.childDateOfBirth
              : dataObject.spousePartnerChildDateOfBirth
            : '';
          const percentage = isBeneficiaryGroup
            ? groupKey === 'children-group'
              ? dataObject.childAmount
              : dataObject.spousePartnerChildAmount
            : '';

          return (
            <React.Fragment key={inst.instanceId ?? idx}>
              <tr>
                <td
                  style={{
                    backgroundColor: '#f8f6f1',
                    color: '#0c1b39',
                    padding: '10px',
                    borderTop: '1px solid #666666',
                  }}
                  width="30%"
                  align="left"
                  colSpan={2}
                >
                  <strong>
                    {escapeHtml(fmt(getCardTitleByGroupKey(allSteps, groupKey) ?? groupKey))}{' '}
                    {formInstances.length > 1 ? idx + 1 : ''}
                  </strong>
                </td>
              </tr>
              {isBeneficiaryGroup ? (
                <>
                  <tr>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="30%"
                      align="left"
                    >
                      Name
                    </td>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="70%"
                      align="left"
                    >
                      {escapeHtml(fmt(name))}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="30%"
                      align="left"
                    >
                      Relationship
                    </td>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="70%"
                      align="left"
                    >
                      {escapeHtml(fmt(relationship))}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="30%"
                      align="left"
                    >
                      Date of birth
                    </td>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="70%"
                      align="left"
                    >
                      {escapeHtml(fmt(dob))}
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="30%"
                      align="left"
                    >
                      Percentage of estate
                    </td>
                    <td
                      style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                      width="70%"
                      align="left"
                    >
                      {escapeHtml(fmt(percentage))}
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  {Object.keys(dataObject).map((key) => {
                    if (key.endsWith('ShortText')) return null;
                    const shortTextKey = `${key}ShortText`;
                    const valueToRender =
                      isExecutorChoiceGroup && dataObject[shortTextKey] !== undefined
                        ? dataObject[shortTextKey]
                        : dataObject[key];
                    const normalizedValue =
                      key === 'countryOfPermanentResidence' && typeof valueToRender === 'string'
                        ? capitalizeWords(valueToRender)
                        : valueToRender;

                    return (
                      <tr key={`${inst.instanceId ?? idx}-${key}`}>
                        <td
                          style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                          width={isExecutorChoiceGroup ? '100%' : '30%'}
                          align="left"
                          colSpan={isExecutorChoiceGroup ? 2 : 1}
                        >
                          {isExecutorChoiceGroup
                            ? escapeHtml(fmt(normalizedValue))
                            : escapeHtml(fmt(getFieldLabelByFieldId(allSteps, key) ?? key))}
                        </td>
                        {!isExecutorChoiceGroup && (
                          <td
                            style={{
                              backgroundColor: '#f8f6f1',
                              color: '#0c1b39',
                              padding: '10px',
                            }}
                            width="70%"
                            align="left"
                          >
                            {escapeHtml(fmt(normalizedValue))}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const WillCapacityNoEmailBody = ({ values, completedForms, allSteps }: Props) => {
  const safeValues = isPlainObject(values) ? values : {};
  const safeCompleted = isPlainObject(completedForms) ? completedForms : {};
  const childInstances: FormInstance[] = Array.isArray(safeCompleted['children-group'])
    ? safeCompleted['children-group']
    : [];
  const spousePartnerChildInstances: FormInstance[] = Array.isArray(
    safeCompleted['spousePartnerChildrenFromPreviousRelationship-group']
  )
    ? safeCompleted['spousePartnerChildrenFromPreviousRelationship-group']
    : [];
  const executorChoiceInstances: FormInstance[] = Array.isArray(
    safeCompleted['executor-group-choice']
  )
    ? safeCompleted['executor-group-choice']
    : [];
  const selectedExecutorChoice =
    executorChoiceInstances[0]?.data?.executorChoice ?? safeValues.executorChoice;
  const spousePartnerExecutorChoices = [
    'I want Irwin Mitchell and my spouse/partner/civil partner to be my Executor jointly',
    "I want my spouse/partner/civil partner to be my Executor. I'd like Irwin Mitchell to act as substitute if my chosen Executor can't carry out their duties for any reason",
  ];
  const isSpousePartnerExecutorChoice =
    typeof selectedExecutorChoice === 'string' &&
    spousePartnerExecutorChoices.includes(selectedExecutorChoice);
  const hasExecutorChoiceGroup = executorChoiceInstances.length > 0;
  const executorValueFields = [
    'executorFirstName',
    'executorMiddleName',
    'executorLastName',
    'executorRelationship',
    'executorHouseNumber',
    'executorStreet',
    'executorTown',
    'executorPostCode',
  ] as const;
  const executorValuesFallbackData = executorValueFields.reduce<UnknownRecord>((acc, fieldId) => {
    const value = safeValues[fieldId];
    if (value !== undefined && value !== null && value !== '') {
      acc[fieldId] = value;
    }
    return acc;
  }, {});
  const hasExecutorValuesFallback = Object.keys(executorValuesFallbackData).length > 0;
  const spousePartnerExecutorFallbackData: UnknownRecord = {};
  const spousePartnerAddress = {
    houseNumber:
      safeValues.spousePartnerHouseNumber ||
      (safeValues.spousePartnerSameAddress === 'Yes' ? safeValues.houseNumber : undefined),
    street:
      safeValues.spousePartnerStreet ||
      (safeValues.spousePartnerSameAddress === 'Yes' ? safeValues.street : undefined),
    town:
      safeValues.spousePartnerTown ||
      (safeValues.spousePartnerSameAddress === 'Yes' ? safeValues.town : undefined),
    postCode:
      safeValues.spousePartnerPostCode ||
      (safeValues.spousePartnerSameAddress === 'Yes' ? safeValues.postCode : undefined),
  };
  if (safeValues.spousePartnerFirstName) {
    spousePartnerExecutorFallbackData.executorFirstName = safeValues.spousePartnerFirstName;
  }
  if (safeValues.spousePartnerMiddleName) {
    spousePartnerExecutorFallbackData.executorMiddleName = safeValues.spousePartnerMiddleName;
  }
  if (safeValues.spousePartnerLastName) {
    spousePartnerExecutorFallbackData.executorLastName = safeValues.spousePartnerLastName;
  }
  spousePartnerExecutorFallbackData.executorRelationship =
    safeValues.spousePartnerRelationship || 'Spouse/Partner/Civil Partner';
  if (spousePartnerAddress.houseNumber) {
    spousePartnerExecutorFallbackData.executorHouseNumber = spousePartnerAddress.houseNumber;
  }
  if (spousePartnerAddress.street) {
    spousePartnerExecutorFallbackData.executorStreet = spousePartnerAddress.street;
  }
  if (spousePartnerAddress.town) {
    spousePartnerExecutorFallbackData.executorTown = spousePartnerAddress.town;
  }
  if (spousePartnerAddress.postCode) {
    spousePartnerExecutorFallbackData.executorPostCode = spousePartnerAddress.postCode;
  }
  const hasSpousePartnerExecutorFallback =
    Object.keys(spousePartnerExecutorFallbackData).length > 0;
  const executorDetailInstances: FormInstance[] =
    Array.isArray(safeCompleted['executor-group']) && safeCompleted['executor-group'].length > 0
      ? safeCompleted['executor-group']
      : hasExecutorValuesFallback
        ? [{ instanceId: 'executor-group-values-fallback', data: executorValuesFallbackData }]
        : isSpousePartnerExecutorChoice && hasSpousePartnerExecutorFallback
          ? [
              {
                instanceId: 'executor-group-spouse-partner-values-fallback',
                data: spousePartnerExecutorFallbackData,
              },
            ]
          : [];
  const groupKey = 'personal-details-group';
  const instances: FormInstance[] = Array.isArray(safeCompleted[groupKey])
    ? safeCompleted[groupKey]
    : [];

  if (!instances.length) return null;

  return (
    <React.Fragment>
      <table
        role="presentation"
        width="100%"
        border={0}
        cellPadding="0"
        cellSpacing="0"
        align="center"
      >
        <tbody>
          <tr>
            <td
              style={{ backgroundColor: '#0c1b39', color: '#ffffff', padding: '10px' }}
              width="100%"
              align="left"
            >
              Will Type
            </td>
          </tr>
          <tr>
            <td
              style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
              width="100%"
              align="left"
            >
              {typeof safeValues.willType === 'string' ? safeValues.willType : ''}
            </td>
          </tr>
          <tr>
            <td style={{ backgroundColor: '#f8f6f1', color: '#0c1b39' }} width="100%" align="left">
              {safeCompleted['personal-details-group'] &&
                safeCompleted['personal-details-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['personal-details-group']}
                    groupKey="personal-details-group"
                    allSteps={allSteps}
                    showSectionHeader={true}
                  />
                )}
              {safeCompleted['children-group'] && safeCompleted['children-group'].length > 0 && (
                <SectionRenderer
                  formInstances={safeCompleted['children-group']}
                  groupKey="children-group"
                  allSteps={allSteps}
                />
              )}
              {safeCompleted['guardian-group'] && safeCompleted['guardian-group'].length > 0 && (
                <SectionRenderer
                  formInstances={safeCompleted['guardian-group']}
                  groupKey="guardian-group"
                  allSteps={allSteps}
                />
              )}
              {safeCompleted['spouse-partner-personal-details-group'] &&
                safeCompleted['spouse-partner-personal-details-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['spouse-partner-personal-details-group']}
                    groupKey="spouse-partner-personal-details-group"
                    allSteps={allSteps}
                    showSectionHeader={true}
                  />
                )}
              {safeCompleted['spousePartnerChildrenFromPreviousRelationship-group'] &&
                safeCompleted['spousePartnerChildrenFromPreviousRelationship-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={
                      safeCompleted['spousePartnerChildrenFromPreviousRelationship-group']
                    }
                    groupKey="spousePartnerChildrenFromPreviousRelationship-group"
                    allSteps={allSteps}
                  />
                )}
              {safeCompleted['spousePartnerGuardian-group'] &&
                safeCompleted['spousePartnerGuardian-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['spousePartnerGuardian-group']}
                    groupKey="spousePartnerGuardian-group"
                    allSteps={allSteps}
                  />
                )}
              {hasExecutorChoiceGroup && (
                <SectionRenderer
                  formInstances={executorChoiceInstances}
                  groupKey="executor-group-choice"
                  allSteps={allSteps}
                  showSectionHeader={true}
                />
              )}
              {executorDetailInstances.length > 0 && (
                <SectionRenderer
                  formInstances={executorDetailInstances}
                  groupKey="executor-group"
                  allSteps={allSteps}
                  showSectionHeader={!hasExecutorChoiceGroup}
                />
              )}
              {safeCompleted['estate-estimation-group'] &&
                safeCompleted['estate-estimation-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['estate-estimation-group']}
                    groupKey="estate-estimation-group"
                    allSteps={allSteps}
                    showSectionHeader={true}
                  />
                )}
              {safeCompleted['business-group'] && safeCompleted['business-group'].length > 0 && (
                <SectionRenderer
                  formInstances={safeCompleted['business-group']}
                  groupKey="business-group"
                  allSteps={allSteps}
                />
              )}
              {safeCompleted['charities-group'] && safeCompleted['charities-group'].length > 0 ? (
                <SectionRenderer
                  formInstances={safeCompleted['charities-group']}
                  groupKey="charities-group"
                  allSteps={allSteps}
                  showSectionHeader={true}
                />
              ) : (
                <EmptyGroupPlaceholder
                  allSteps={allSteps}
                  groupKey="charities-group"
                  showSectionHeader={true}
                />
              )}
              {safeCompleted['cash-gift-group'] && safeCompleted['cash-gift-group'].length > 0 && (
                <SectionRenderer
                  formInstances={safeCompleted['cash-gift-group']}
                  groupKey="cash-gift-group"
                  allSteps={allSteps}
                />
              )}
              {safeCompleted['cash-gift-to-someone-group'] &&
              safeCompleted['cash-gift-to-someone-group'].length > 0 ? (
                <SectionRenderer
                  formInstances={safeCompleted['cash-gift-to-someone-group']}
                  groupKey="cash-gift-to-someone-group"
                  allSteps={allSteps}
                />
              ) : (
                <EmptyGroupPlaceholder allSteps={allSteps} groupKey="cash-gift-to-someone-group" />
              )}
              {safeCompleted['distribution-estate-group'] &&
                safeCompleted['distribution-estate-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['distribution-estate-group']}
                    groupKey="distribution-estate-group"
                    allSteps={allSteps}
                  />
                )}
              {childInstances.length > 0 && (
                <SectionRenderer
                  formInstances={childInstances}
                  groupKey="children-group"
                  allSteps={allSteps}
                />
              )}
              {spousePartnerChildInstances.length > 0 && (
                <SectionRenderer
                  formInstances={spousePartnerChildInstances}
                  groupKey="spousePartnerChildrenFromPreviousRelationship-group"
                  allSteps={allSteps}
                />
              )}
              {safeCompleted['other-information-estate-group'] &&
                safeCompleted['other-information-estate-group'].length > 0 && (
                  <SectionRenderer
                    formInstances={safeCompleted['other-information-estate-group']}
                    groupKey="other-information-estate-group"
                    allSteps={allSteps}
                  />
                )}
            </td>
          </tr>
          <tr>
            <td
              colSpan={2}
              style={{ backgroundColor: '#0c1b39', color: '#ffffff', padding: '10px' }}
              width="100%"
              align="left"
            >
              {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'completedBy')))}
            </td>
          </tr>
          <tr>
            <td
              colSpan={2}
              style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
              width="100%"
              align="left"
            >
              {typeof safeValues.completedBy === 'string'
                ? escapeHtml(fmt(safeValues.completedBy))
                : ''}
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              {safeValues.completedBy === 'Somebody Else' && (
                <React.Fragment key="completedByName">
                  <table width="100%" border={0} cellPadding={0} cellSpacing={0} align="left">
                    <tr>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="30%"
                        align="left"
                      >
                        {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'completedByFirstName')))}
                      </td>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="70%"
                        align="left"
                      >
                        {typeof safeValues.completedByFirstName === 'string'
                          ? escapeHtml(fmt(safeValues.completedByFirstName))
                          : ''}
                      </td>
                    </tr>
                    {typeof safeValues.completedByMiddleName === 'string' &&
                      safeValues.completedByMiddleName && (
                        <tr>
                          <td
                            style={{
                              backgroundColor: '#f8f6f1',
                              color: '#0c1b39',
                              padding: '10px',
                            }}
                            width="30%"
                            align="left"
                          >
                            {escapeHtml(
                              fmt(getFieldLabelByFieldId(allSteps, 'completedByMiddleName'))
                            )}
                          </td>
                          <td
                            style={{
                              backgroundColor: '#f8f6f1',
                              color: '#0c1b39',
                              padding: '10px',
                            }}
                            width="70%"
                            align="left"
                          >
                            {typeof safeValues.completedByMiddleName === 'string'
                              ? escapeHtml(fmt(safeValues.completedByMiddleName))
                              : ''}
                          </td>
                        </tr>
                      )}
                    <tr>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="30%"
                        align="left"
                      >
                        {escapeHtml(fmt(getFieldLabelByFieldId(allSteps, 'completedByLastName')))}
                      </td>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="70%"
                        align="left"
                      >
                        {typeof safeValues.completedByLastName === 'string'
                          ? escapeHtml(fmt(safeValues.completedByLastName))
                          : ''}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="30%"
                        align="left"
                      >
                        {escapeHtml(
                          fmt(getFieldLabelByFieldId(allSteps, 'completedByRelationship'))
                        )}
                      </td>
                      <td
                        style={{ backgroundColor: '#f8f6f1', color: '#0c1b39', padding: '10px' }}
                        width="70%"
                        align="left"
                      >
                        {typeof safeValues.completedByRelationship === 'string'
                          ? escapeHtml(fmt(safeValues.completedByRelationship))
                          : ''}
                      </td>
                    </tr>
                  </table>
                </React.Fragment>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </React.Fragment>
  );
};

export default WillCapacityNoEmailBody;
